/**
 * ServiceOrderFieldServiceDialog - "Adicionar serviço" vendido em campo (BR-OS-048).
 *
 * Sem sessão: recebe o catálogo de serviços e a função de gravação (desktop e portal).
 * O serviço entra como "Aguardando análise" e nunca altera o negócio.
 *
 * RELEASE: 2026-10-18 v1.3.0 - Textos opcionais (descrição e rótulo da observação) para o modo operador.
 * RELEASE: 2026-10-17 v1.2.0 - Campo Categoria filtra a lista de serviços.
 * RELEASE: 2026-10-17 v1.1.0 - Lista agrupada por categoria/subcategoria.
 * RELEASE: 2026-10-17 v1.0.0 - Versão inicial.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { FieldLabel } from '@/components/common/FormSection';
import { opcoesCategoria, filtrarPorCategoria, type ServiceProductOption } from '@/lib/serviceOrders/serviceProductGroups';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: ServiceProductOption[];
  loading?: boolean;
  saving?: boolean;
  onConfirm: (a: { productId: string; quantity: number; notes: string }) => Promise<void> | void;
  /** v1.3.0: texto abaixo do título (padrão: fluxo do prestador) */
  description?: string;
  /** v1.3.0: rótulo do campo de observação */
  notesLabel?: string;
}

export function ServiceOrderFieldServiceDialog({ open, onOpenChange, products, loading, saving, onConfirm, description = 'O serviço fica aguardando análise do gestor.', notesLabel = 'Observação' }: Props) {
  const [productId, setProductId] = useState<string | null>(null);
  // v1.2.0: filtro de categoria/subcategoria que restringe a lista de serviços
  const [categoria, setCategoria] = useState<string | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  useEffect(() => { if (open) { setProductId(null); setCategoria(null); setQuantity('1'); setNotes(''); } }, [open]);

  const categorias = useMemo(() => opcoesCategoria(products), [products]);
  const filtrados = useMemo(() => filtrarPorCategoria(products, categoria), [products, categoria]);

  /** Troca a categoria e limpa o serviço se ele não pertencer a ela. */
  const trocarCategoria = (c: string | null) => {
    setCategoria(c);
    if (productId && !filtrarPorCategoria(products, c).some((p) => p.id === productId)) setProductId(null);
  };

  const qty = Number(quantity.replace(',', '.'));
  const valid = !!productId && qty > 0 && qty <= 9999;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar serviço</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <FieldLabel>Categoria</FieldLabel>
            <SearchableSelect value={categoria} onChange={trocarCategoria} isLoading={loading}
              noneLabel="Todas as categorias" options={categorias} />
          </div>
          <div className="space-y-1.5">
            <FieldLabel required>Serviço</FieldLabel>
            <SearchableSelect value={productId} onChange={setProductId} isLoading={loading} placeholder="Selecionar serviço..."
              options={filtrados.map((p) => ({ value: p.id, label: p.name, hint: p.sku }))} />
          </div>
          <div className="space-y-1.5">
            <FieldLabel required>Quantidade</FieldLabel>
            <Input inputMode="decimal" maxLength={7} value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/[^\d.,]/g, ''))} />
          </div>
          <div className="space-y-1.5">
            <FieldLabel>{notesLabel}</FieldLabel>
            <Textarea rows={3} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={!valid || saving} onClick={() => productId && onConfirm({ productId, quantity: qty, notes })}>Adicionar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
